import { useEffect, useMemo, useState } from 'react';
import { bankingAPI, reconciliationAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { AllocationTargetPicker } from './AllocationTargetPicker';
import { MatchTypeChip } from './MatchTypeChip';
import { useToast } from '../../hooks/useToast';

const MATCH_TYPES_IN = [
  { value: 'invoice_payment', label: 'Customer invoice payment' },
  { value: 'refund_supplier', label: 'Supplier refund (money in)' },
  { value: 'bank_interest', label: 'Bank interest' },
  { value: 'proprietor_contribution_in', label: 'Proprietor contribution' },
  { value: 'internal_transfer', label: 'Transfer in' },
  { value: 'loan_drawdown', label: 'Loan drawdown' },
  { value: 'manual_adjustment', label: 'Manual adjustment' },
  { value: 'opening_balance', label: 'Opening balance' },
  { value: 'other', label: 'Other (money in)' },
];

const MATCH_TYPES_OUT = [
  { value: 'purchase_payment', label: 'Supplier / purchase payment' },
  { value: 'refund_customer', label: 'Customer refund (money out)' },
  { value: 'bank_charge', label: 'Bank charge / fee' },
  { value: 'proprietor_drawing_out', label: 'Proprietor drawing' },
  { value: 'personal_reimbursement', label: 'Reimburse proprietor' },
  { value: 'internal_transfer', label: 'Transfer out' },
  { value: 'credit_card_settlement', label: 'Credit card settlement' },
  { value: 'loan_repayment', label: 'Loan repayment' },
  { value: 'manual_adjustment', label: 'Manual adjustment' },
  { value: 'other', label: 'Other (money out)' },
];

const STRATEGY_LABELS = {
  auto_suggested: 'Auto-suggested',
  manual: 'Manual',
  rule_based: 'Rule-based',
};

const TARGET_TYPE_LABELS = {
  invoice_payment: 'Invoice payment',
  purchase_payment: 'Purchase payment',
  bank_transaction: 'Bank transaction',
  loan_schedule_line: 'Loan schedule line',
  credit_card_settlement: 'Credit card settlement',
  proprietor_drawing_log: 'Proprietor drawing',
  nominal_category: 'Nominal category',
  new_internal_transaction: 'New internal transaction',
};

function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toISOString().slice(0, 10);
}

/**
 * Review / edit / confirm / reject a draft reconciliation match.
 *
 * Opened from the Matched tab when the user clicks Review on a statement
 * line that has a draft match (manual, auto-suggested, or rule-based).
 */
export function DraftMatchReviewModal({
  open,
  onOpenChange,
  line,
  bankAccountId,
  onUpdated,
}) {
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [match, setMatch] = useState(null);
  const [matchAllocations, setMatchAllocations] = useState([]);
  const [statementLine, setStatementLine] = useState(null);
  const [bankTxDetail, setBankTxDetail] = useState(null);

  const [matchType, setMatchType] = useState('');
  const [matchDate, setMatchDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [allocatedAmount, setAllocatedAmount] = useState('');
  const [target, setTarget] = useState({
    target_type: 'bank_transaction',
    target_id: null,
    nominal_account_id: null,
    description: '',
  });

  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const displayLine = statementLine || line;
  const lineAmount = Number(displayLine?.amount ?? 0);
  const isInflow = lineAmount > 0;
  const matchTypeOptions = isInflow ? MATCH_TYPES_IN : MATCH_TYPES_OUT;
  const isSingleAllocation = matchAllocations.length === 1;
  const isDraft = match?.status === 'draft';

  useEffect(() => {
    if (!open || !line?.id) return;

    let cancelled = false;

    const resetForm = () => {
      setError('');
      setBusy(false);
      setMatch(null);
      setMatchAllocations([]);
      setStatementLine(null);
      setBankTxDetail(null);
      setShowRejectReason(false);
      setRejectReason('');
    };

    const load = async () => {
      resetForm();
      setLoading(true);
      try {
        const lineData = await reconciliationAPI.getStatementLine(line.id);
        if (cancelled) return;

        const sl = lineData.statement_line;
        setStatementLine(sl);

        const lineAllocs = lineData.allocations || [];
        const primaryAlloc = lineAllocs.find((a) => a.match_status !== 'voided') || lineAllocs[0];
        if (!primaryAlloc?.match_id) {
          setError('No active match found for this statement line.');
          return;
        }

        const matchData = await reconciliationAPI.getMatch(primaryAlloc.match_id);
        if (cancelled) return;

        const m = matchData.match;
        const allocs = matchData.allocations || [];
        setMatch(m);
        setMatchAllocations(allocs);

        setMatchType(m.match_type || '');
        setMatchDate(m.match_date ? String(m.match_date).slice(0, 10) : '');
        setReference(m.reference || '');
        setNotes(m.notes || '');

        if (allocs.length === 1) {
          const alloc = allocs[0];
          setAllocatedAmount(String(Math.abs(Number(alloc.allocated_amount)).toFixed(2)));
          setTarget({
            target_type: alloc.target_type || 'bank_transaction',
            target_id: alloc.target_id || null,
            nominal_account_id: alloc.nominal_account_id || null,
            description: alloc.description || '',
          });

          if (alloc.target_type === 'bank_transaction' && alloc.target_id) {
            try {
              const tx = await bankingAPI.getTransaction(alloc.target_id);
              if (!cancelled) setBankTxDetail(tx);
            } catch (_) {
              /* non-fatal enrichment */
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err.message || 'Could not load match');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, line?.id]);

  const canSave = useMemo(() => {
    if (!match || !isDraft || !bankAccountId) return false;
    if (!matchType) return false;
    if (isSingleAllocation) {
      const amt = Number(allocatedAmount);
      if (!Number.isFinite(amt) || amt <= 0) return false;
      if (target.target_type === 'nominal_category') return !!target.nominal_account_id;
      return !!target.target_id;
    }
    return true;
  }, [match, isDraft, bankAccountId, matchType, isSingleAllocation, allocatedAmount, target]);

  const handleSave = async () => {
    if (!canSave || !match?.id) return;
    setError('');
    setBusy(true);
    try {
      const payload = {
        match_type: matchType,
        match_date: matchDate || undefined,
        reference: reference || null,
        notes: notes || null,
      };

      if (isSingleAllocation) {
        const alloc = matchAllocations[0];
        payload.allocations = [
          {
            statement_line_id: alloc.statement_line_id || line.id,
            allocated_amount: Number(allocatedAmount),
            target_type: target.target_type,
            target_id: target.target_id || null,
            nominal_account_id: target.nominal_account_id || null,
            description: target.description || null,
          },
        ];
      }

      await reconciliationAPI.updateMatch(match.id, payload);
      toastSuccess('Match updated', 'Draft match saved.');
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not save match');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!match?.id || !isDraft) return;
    const ok = window.confirm(
      'This will post to the ledger and finalise the match. This action cannot be undone by staff — only an admin can unreconcile later.\n\nProceed?'
    );
    if (!ok) return;

    setError('');
    setBusy(true);
    try {
      await reconciliationAPI.reconcileMatch(match.id);
      toastSuccess('Match confirmed', 'The match has been reconciled and posted to the ledger.');
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not confirm match');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!match?.id || !isDraft) return;

    const reason = rejectReason.trim();
    if (!reason) {
      setError('A reason is required to reject the match (1–1000 characters).');
      return;
    }
    if (reason.length > 1000) {
      setError('Reason must be 1000 characters or fewer.');
      return;
    }

    setError('');
    setBusy(true);
    try {
      await reconciliationAPI.voidMatch(match.id, reason);
      toastSuccess('Match rejected', 'The draft match was voided; the line returns to the queue.');
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not reject match');
    } finally {
      setBusy(false);
    }
  };

  const strategyLabel = STRATEGY_LABELS[match?.match_strategy] || match?.match_strategy;

  return (
    <>
      <ToastContainer />
      <SlideOver open={open} onOpenChange={onOpenChange} title="Review draft match">
        {!line ? (
          <div className="text-xs text-gray-500 dark:text-gray-400">No statement line selected.</div>
        ) : loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading match…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Statement line summary */}
            <Card>
              <CardContent className="p-3 text-xs">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Date
                    </div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {formatDate(displayLine?.transaction_date)}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Description
                    </div>
                    <div
                      className="truncate text-gray-900 dark:text-gray-100"
                      title={displayLine?.description || ''}
                    >
                      {displayLine?.description || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Reference
                    </div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {displayLine?.reference || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Counterparty
                    </div>
                    <div className="truncate text-gray-900 dark:text-gray-100">
                      {displayLine?.counterparty || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Amount
                    </div>
                    <div
                      className={
                        lineAmount >= 0
                          ? 'font-semibold text-green-700 dark:text-green-400'
                          : 'font-semibold text-red-700 dark:text-red-400'
                      }
                    >
                      {formatMoney(lineAmount)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {error && (
              <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </CardContent>
              </Card>
            )}

            {!isDraft && match ? (
              <Card>
                <CardContent className="p-4 text-xs text-gray-700 dark:text-gray-300">
                  <p>
                    This match is <strong>{match.status}</strong> and cannot be edited here.
                    {match.status === 'reconciled'
                      ? ' Only an admin can unreconcile it.'
                      : ' Create a new match if needed.'}
                  </p>
                  <div className="mt-3">
                    <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>
                      Close
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : match ? (
              <>
                {/* Match header */}
                <div className="flex flex-wrap items-center gap-2">
                  <MatchTypeChip matchType={match.match_type} />
                  {strategyLabel && (
                    <Badge variant="neutral">{strategyLabel}</Badge>
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Draft · {formatMoney(match.total_amount)}
                  </span>
                </div>

                {/* Editable metadata */}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <FormLabel>Match type</FormLabel>
                    <Select
                      value={matchType}
                      onChange={(e) => setMatchType(e.target.value)}
                      disabled={busy}
                    >
                      {matchTypeOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <FormLabel>Match date</FormLabel>
                    <Input
                      type="date"
                      value={matchDate}
                      onChange={(e) => setMatchDate(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                  <div>
                    <FormLabel>Reference</FormLabel>
                    <Input
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="e.g. INV-1234"
                      disabled={busy}
                    />
                  </div>
                  <div>
                    <FormLabel>Notes</FormLabel>
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Internal memo"
                      disabled={busy}
                    />
                  </div>
                </div>

                {/* Allocations */}
                {isSingleAllocation ? (
                  <div className="space-y-3">
                    <div>
                      <FormLabel>Allocated amount</FormLabel>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocatedAmount}
                        onChange={(e) => setAllocatedAmount(e.target.value)}
                        disabled={busy}
                      />
                    </div>
                    <div>
                      <FormLabel>Allocation target</FormLabel>
                      <AllocationTargetPicker
                        bankAccountId={bankAccountId}
                        lineAmount={lineAmount}
                        value={target}
                        onChange={setTarget}
                      />
                    </div>
                    {bankTxDetail && target.target_type === 'bank_transaction' && (
                      <Card>
                        <CardContent className="p-3 text-xs text-gray-700 dark:text-gray-300">
                          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Linked bank transaction
                          </div>
                          <div className="mt-1 grid grid-cols-3 gap-2">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Date </span>
                              {formatDate(bankTxDetail.transaction_date)}
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-500 dark:text-gray-400">Description </span>
                              {bankTxDetail.description || '—'}
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Amount </span>
                              {formatMoney(bankTxDetail.amount)}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ) : matchAllocations.length > 1 ? (
                  <div>
                    <FormLabel>Allocations (split match)</FormLabel>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      To change split targets, reject this match and create a new split.
                    </p>
                    <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                              Target type
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                              Amount
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {matchAllocations.map((alloc) => (
                            <tr key={alloc.id}>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                {TARGET_TYPE_LABELS[alloc.target_type] || alloc.target_type}
                              </td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                {formatMoney(alloc.allocated_amount)}
                              </td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                {alloc.description || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {/* Reject reason (inline) */}
                {showRejectReason && (
                  <div>
                    <FormLabel>Rejection reason</FormLabel>
                    <Input
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Why is this match being rejected?"
                      disabled={busy}
                    />
                  </div>
                )}

                {/* Footer actions */}
                <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
                  <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                    Cancel
                  </Button>
                  {!showRejectReason ? (
                    <Button
                      variant="outline"
                      className="text-red-700 dark:text-red-400"
                      onClick={() => {
                        setShowRejectReason(true);
                        setError('');
                      }}
                      disabled={busy}
                    >
                      Reject
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="text-red-700 dark:text-red-400"
                      onClick={handleReject}
                      disabled={busy || !rejectReason.trim()}
                    >
                      {busy ? 'Rejecting…' : 'Confirm reject'}
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleSave} disabled={!canSave || busy}>
                    {busy ? 'Saving…' : 'Save changes'}
                  </Button>
                  <Button onClick={handleConfirm} disabled={busy}>
                    {busy ? 'Confirming…' : 'Confirm'}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </SlideOver>
    </>
  );
}

export default DraftMatchReviewModal;
